import { type Observable as ObservableType, Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/of';
import 'rxjs/add/operator/mergeMap';
import { UNASSIGNED_EPIC_ID } from '../../../common/constants';
import type { FieldConfigs } from '../../../common/hooks/use-store-fields/types.tsx';
import { SPRINT } from '../../../model/constants';
import type { CardId, Card, CardList } from '../../../model/types';
import cardCreateRest from '../../../rest/card-create-multiple-gql';
import type { NewCardGql, CardCreateGql } from '../../../rest/card-create-multiple-gql/types';
import {
	getBaseUrl,
	getRapidViewId,
	getStoryPointFieldId,
	projectIdSelector,
} from '../../../state/selectors/app';
import {
	getMostRecentAssigneeFilter,
	getAppliedLabelFilters,
} from '../../../state/selectors/card-create';
import { getCardLists } from '../../../state/selectors/cards';
import type { MiddlewareAPI } from '../../../state/types';

const transformer = (
	{
		id,
		done: isDone,
		flagged: isFlagged,
		parentId,
		fixVersions,

		issue: {
			key,
			summary,
			type: { id: cardTypeId, iconUrl: cardTypeUrl },
			labels,
			status: { id: statusId },
			assignee,
		},

		priority,
		dueDate,
	}: NewCardGql,
	projectId: number,
): Card => ({
	id: Number(id),
	key,
	summary,
	icon: cardTypeUrl,
	typeId: Number(cardTypeId),
	isDone,
	labels,
	isFlagged,
	assignee: assignee
		? {
				id: assignee.accountId,
				displayName: assignee.displayName,
				avatarUrl: assignee.avatarUrl,
			}
		: null,
	issueUrl: `/browse/${key}`,
	statusId,
	parentId: parentId || '' ? Number(parentId) : null,
	fixVersions: fixVersions || [],
	priority,
	dueDate,
	estimate: null, // Initial estimate is always NULL for created card
	numCompleteChildren: 0,
	numTotalChildren: 0,
	projectId,
});

export const createMultipleCardService = (
	// TODO: Passing the store is against the architecture, use service-context instead and request objects;
	// additionally, don't pass in a callback parameter
	store: MiddlewareAPI,
	summaries: string[],
	issueTypeId: number,
	parentId: number | null,
	fixVersions: string[],
	cardListId: string,
	rankBeforeCardId: CardId | null = null,
	storeFieldsFromCardCreateData: (
		cardCreateData: CardCreateGql,
		fieldConfigs: FieldConfigs,
	) => void,
): ObservableType<Card[] | null> => {
	const state = store.getState();
	const baseUrl = getBaseUrl(state);
	const boardId = getRapidViewId(state);
	const storyPointFieldId = getStoryPointFieldId(state);
	const assignee = getMostRecentAssigneeFilter(state);
	const labels = getAppliedLabelFilters(state);
	// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
	const cardList = getCardLists(state)[cardListId] as CardList;
	const projectId = projectIdSelector(state);

	return cardCreateRest(baseUrl, {
		boardId,
		destination: cardList.type,
		destinationId: cardList.type === SPRINT ? cardList.id : null,
		newCards: summaries.map((summary) => ({
			summary,
			issueTypeId,
			parentId: parentId === UNASSIGNED_EPIC_ID ? null : parentId, // NOTE: The "issues without epic" filter sets parentId to 0 which is mapped back to null
			fixVersions,
			assigneeId: assignee ? assignee.id : null,
			labels: labels || [],
		})),
		rankBeforeCardId,
		// @ts-expect-error - TS2345 - Argument of type '(result: CardCreateGql) => ObservableType<null> | ObservableType<Card[]>' is not assignable to parameter of type '(value: CardCreateGql, index: number) => ObservableInput<Card[] | null>'.
	}).flatMap((result: CardCreateGql) => {
		const fieldConfigs: FieldConfigs = { storyPointFieldId };

		// Pre-populate extracted fields (Sweet State) using card create data
		storeFieldsFromCardCreateData(result, fieldConfigs);
		if (result.planModeCardCreate.newCards && result.planModeCardCreate.newCards.length > 0) {
			const transformedCards = result.planModeCardCreate.newCards.map((card) =>
				transformer(card, projectId),
			);
			return Observable.of(transformedCards);
		}
		return Observable.of(null);
	});
};
